import { Play } from 'lucide-react'
import { useState } from 'react'

import type { Media } from '@/api/types'
import { useT } from '@/i18n'
import { formatDuration } from '@/lib/format'

/**
 * 帖子里的视频（Phase 3h）。
 *
 * 先只放封面 + 播放键 + 时长，点了才换成 <video>：时间线里不自动播放、不预加载，
 * 翻一屏不该偷偷下几十 MB。点击不冒泡到帖子卡片（那会跳进详情）。
 */
export function VideoPlayer({ media, aspect }: { media: Media; aspect?: string }) {
  const t = useT()
  const [playing, setPlaying] = useState(false)
  const label = media.alt_text || t('media.playVideo')

  if (!media.url) {
    // 理论上看不到：转完才能发帖（D93）。万一有，就如实说
    return (
      <div
        className="flex size-full items-center justify-center bg-surface-subtle text-[13px] text-ink-muted"
        style={{ aspectRatio: aspect }}
      >
        {media.status === 'failed' ? t('media.videoFailed') : t('media.videoProcessing')}
      </div>
    )
  }

  if (playing) {
    return (
      <video
        src={media.url}
        poster={media.poster_url ?? undefined}
        controls
        autoPlay
        playsInline
        aria-label={label}
        onClick={(event) => event.stopPropagation()}
        className="size-full bg-black object-contain"
        style={{ aspectRatio: aspect }}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        setPlaying(true)
      }}
      aria-label={label}
      className="group relative block size-full bg-black"
      style={{ aspectRatio: aspect }}
    >
      {media.poster_url && (
        <img src={media.poster_url} alt="" loading="lazy" className="size-full object-contain" />
      )}
      <span className="absolute inset-0 m-auto flex size-14 items-center justify-center px-corners [--px-step:3px] bg-brand text-on-brand transition-transform group-hover:scale-105">
        <Play className="size-7 translate-x-0.5" fill="currentColor" aria-hidden />
      </span>
      <span className="absolute bottom-2 left-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[12px] font-bold text-white">
        {formatDuration(media.duration_ms)}
      </span>
      {media.alt_text && (
        <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1 text-[11px] font-bold text-white">
          ALT
        </span>
      )}
    </button>
  )
}
